<template>
    <div class="full-page">
        <div class="window loading-screen" v-if="!practiceLoaded">
            <span class="top-text loading-text">Loading Practice Data...</span>
            <div class="lds-dual-ring"></div>
        </div>
        <div class="window" v-else-if="loadError">
            <span class="title" style="font-size: 2em; margin-bottom: 1em;">Error Loading Practice</span>
            <span class="subtitle" style="text-align: center">{{ this.errorMessage }}</span>
        </div>
        <div class="window" v-else>
            <span class="top-text">Edit Plans</span>
            <span class="subtitle">for {{ practiceName }}</span>

            <!-- Plan Selection -->
            <div class="two-column-row">
                <div class="label-container">
                    <span class="attribute-name">Available Plans:</span>
                </div>
                <div class="select-container">
                    <select v-model="selectedPlanId" class="input-class hover-grow" style="cursor: pointer">
                        <option disabled value="">Select a Plan</option>
                        <option v-for="plan in plans" :key="plan.id" :value="plan.id">
                            {{ plan.name }}
                        </option>
                    </select>
                </div>
            </div>

            <!-- Benefits List -->
            <div class="benefits-section" v-if="workingPlan">
                <h2 class="benefits-title">Benefits</h2>
                <div class="benefits-list">
                    <div v-for="(benefit, index) in workingPlan.benefits" :key="index" class="benefit-item">
                        <div class="benefit-header">
                            <span class="benefit-name">{{ benefit.name }}</span>
                            <div class="benefit-actions">
                                <span class="benefit-code">{{ benefit.code }}</span>
                                <i class="pi pi-trash delete hover-grow" @click="removeBenefit(benefit)"></i>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="button-container">
                    <button class="add-btn hover-grow" @click="showAddBenefitModal = true">
                        + Add Benefit
                    </button>
                </div>
            </div>

            <button class="submit-btn hover-grow" @click="showSaveModal = true" v-if="workingPlan">
                Save Changes
            </button>

            <div class="submit submit-error" v-if="showError">
                {{ errorMessage }}
            </div>
            <div class="submit submit-success" v-if="showSuccess">
                Changes saved successfully!
            </div>
        </div>

        <!-- Modal Overlay -->
        <div class="modal-overlay" v-if="showSaveModal" @click.self="showSaveModal = false">
            <SavePlanModal 
                ref="saveModal"
                @exitModal="showSaveModal = false"
                @saveChanges="handleSaveWithBenefits"
                :originalPlan="plans.find(p => p.id === selectedPlanId)"
                :workingPlan="workingPlan"
                :integrationsEnabled="integrationsEnabled"
                :allBenefits="allBenefits"
            />
        </div>

        <!-- Add Benefit Modal -->
        <div class="modal-overlay" v-if="showAddBenefitModal" @click.self="showAddBenefitModal = false">
            <AddBenefitModal 
                @exitModal="showAddBenefitModal = false"
                @benefitAdded="handleBenefitAdded"
                :planType="selectedPlanId"
                :availableBenefits="availableBenefits"
            />
        </div>
    </div>
</template>

<script>
import SavePlanModal from './SavePlanModal.vue';
import AddBenefitModal from '../Onboarding/components/modals/AddBenefitModal.vue';
import { apiService } from '@/api/config';

export default {
    components: {
        SavePlanModal,
        AddBenefitModal
    },
    props: {
        practiceID: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            practiceName: "",
            practiceLoaded: false,
            loadError: false,
            allBenefits: [], // Master list of all available benefits
            plans: [], // Will be populated from API
            selectedPlanId: "",
            workingPlan: null,
            showError: false,
            showSuccess: false,
            errorMessage: "",
            showSaveModal: false,
            showAddBenefitModal: false,
            integrationsEnabled: false,
        }
    },
    watch: {
        selectedPlanId: {
            handler(newId) {
                if (newId) {
                    const selectedPlan = this.plans.find(p => p.id === newId);
                    if (selectedPlan) {
                        this.workingPlan = JSON.parse(JSON.stringify(selectedPlan));
                    }
                } else {
                    this.workingPlan = null;
                }
            },
            immediate: true
        }
    },
    methods: {
        async loadPracticeData() {
            try {
                console.log(`Loading practice data for ID: ${this.practiceID}`);
                const response = await apiService.getCompanyDetails(this.practiceID);
                
                if (response === 404) {
                    this.practiceLoaded = true;
                    this.loadError = true;
                    throw new Error('Practice with ID ' + this.practiceID + ' not found');
                }
                if (response === 500) {
                    this.practiceLoaded = true;
                    this.loadError = true;
                    throw new Error('Server error while loading practice data');
                }

                // Transform the data
                this.practiceName = response.practice.name;

                this.integrationsEnabled = response.practice.integrations_enabled;
                
                // Transform plans data
                this.plans = response.practice.plans.map(plan => ({
                    id: plan.id,
                    name: plan.display_name || plan.plan_name,
                    benefits: plan.benefits.map(benefit => ({
                        id: benefit.id,
                        name: benefit.name,
                        code: benefit.ada_code || benefit.code,
                        technical_name: benefit.technical_name,
                    }))
                }));

                // Transform available benefits
                this.allBenefits = response.practice.available_benefits.map(benefit => ({
                    id: benefit.id,
                    name: benefit.name,
                    code: benefit.ada_code || benefit.code,
                    technical_name: benefit.technical_name,
                }));

                this.practiceLoaded = true;
            } catch (error) {
                console.error('Error loading practice data:', error);
                this.loadError = true;
                this.errorMessage = error.message || "Failed to load practice data";
                this.showError = true;
            }
        },
        async handleSaveWithBenefits(changes) {
            try {
                console.log('Saving changes:', changes);
                
                // Make the API call
                const response = await apiService.getCompanyDetails(this.practiceID, {
                    method: 'POST',
                    data: {
                        planID: changes.planID,
                        benefitsToAdd: changes.benefitsToAdd,
                        benefitsToRemove: changes.benefitsToRemove,
                        syncBenefits: changes.syncBenefits
                    }
                });

                if (response === 404) {
                    throw new Error('Plan not found');
                }
                if (response === 500) {
                    throw new Error('Server error while saving changes');
                }
                
                // Reload practice data to get updated state
                await this.loadPracticeData();
                
                this.showSuccess = true;
                this.showError = false;
                this.showSaveModal = false;
                setTimeout(() => {
                    this.showSuccess = false;
                }, 3000);
            } catch (error) {
                console.error('Error saving changes:', error);
                this.showError = true;
                this.errorMessage = error.message || "Failed to save changes";
                this.showSuccess = false;
                this.showSaveModal = false;
            } finally {
                if (this.$refs.saveModal) {
                    this.$refs.saveModal.isSaving = false;
                }
            }
        },
        handleBenefitAdded(benefit) {
            if (!this.workingPlan.benefits) {
                this.workingPlan.benefits = [];
            }
            this.workingPlan.benefits.push(benefit);
            this.showAddBenefitModal = false;
        },
        removeBenefit(benefit) {
            if (!this.workingPlan) return;
            
            this.workingPlan.benefits = this.workingPlan.benefits.filter(b => b.id !== benefit.id);
        }
    },
    computed: {
        availableBenefits() {
            // Filter out benefits that are already in the working plan
            if (!this.workingPlan) return this.allBenefits;
            
            return this.allBenefits
                .filter(benefit => !this.workingPlan.benefits.some(b => b.id === benefit.id))
                .sort((a, b) => {
                    // Handle null/empty codes by putting them at the end
                    if (!a.code && !b.code) return 0;
                    if (!a.code) return 1;
                    if (!b.code) return -1;
                    
                    const codeA = a.code.split(',')[0].trim();
                    const codeB = b.code.split(',')[0].trim();
                    return codeA.localeCompare(codeB);
                });
        }
    },
    mounted() {
        this.loadPracticeData();
    }
}
</script>

<style scoped>
.full-page {
    width: 100vw;
    min-height: 100vh;
    background-color: var(--color-teal-background);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 2em;
}

.window {
    background-color: white;
    width: 40%;
    min-height: fit-content;
    display: flex;
    flex-direction: column;
    align-items: center;
    border-radius: 10px;
    border: 3px solid var(--font-color-teal);
    padding: 2em;
    margin-bottom: 2em;
}

.top-text {
    color: var(--font-color-teal);
    font-size: 3em;
}

.subtitle {
    color: var(--color-dark-blue);
    font-size: 1.2em;
    margin-bottom: 0.5em;
}

.two-column-row {
    display: flex;
    flex-direction: row;
    width: 75%;
    justify-content: space-between;
    align-items: center;
    margin: 0.5em;
}

.label-container {
    flex: 1;
    display: flex;
    align-items: center;
}

.select-container {
    flex: 1;
    display: flex;
    align-items: center;
}

.attribute-name {
    font-size: 1.2em;
    color: var(--color-dark-blue);
}

.input-class {
    font-size: 1.05em;
    width: 100%;
    border: 2px solid var(--color-medium-light-gray);
    border-radius: 5px;
    padding: 0.5em;
}

.input-class:hover {
    outline: none;
    border: 2px solid var(--color-light-blue);
}

.input-class:focus {
    outline: none;
}

.benefits-section {
    width: 75%;
}

.benefits-title {
    color: var(--color-dark-blue);
    font-size: 1.5em;
    margin-top: 0.5em;
}

.benefits-list {
    display: flex;
    flex-direction: column;
    gap: 1em;
}

.benefit-item {
    background-color: #f5f5f5;
    border-radius: 8px;
    padding: 1em;
    border: 1px solid var(--color-medium-light-gray);
}

.benefit-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.benefit-name {
    font-size: 1.1em;
    color: var(--color-dark-blue);
}

.benefit-code {
    color: var(--color-bright-blue);
    font-weight: bold;
    text-align: right;
}

.submit-btn {
    background-color: transparent;
    outline: none;
    border: 3px solid var(--color-medium-light-gray);
    font-size: 1.5em;
    border-radius: 50px;
    cursor: pointer;
    padding: 0.25em 1em;
    color: var(--color-dark-blue);
    font-weight: 900;
    margin-top: 2em;
}

.submit-btn:hover {
    border: 3px solid var(--color-dark-blue);
}

.submit {
    margin-top: 1em;
    text-align: center;
    font-weight: 900;
}

.submit-error {
    color: var(--color-medium-red);
}

.submit-success {
    color: var(--color-bright-blue);
    font-size: 1.2em;
}

.loading-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2em;
}

.loading-text {
    text-align: center;
}

.lds-dual-ring {
    display: flex;
    justify-content: center;
    align-items: center;
}

.lds-dual-ring:after {
    content: " ";
    display: block;
    width: 200px;
    height: 200px;
    margin: 8px;
    border-radius: 50%;
    border: 12px solid transparent;
    border-color: var(--color-dark-blue) transparent var(--color-dark-blue) transparent;
    animation: lds-dual-ring 1.2s linear infinite;
}

@keyframes lds-dual-ring {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.button-container {
    display: flex;
    justify-content: center;
    width: 100%;
    margin-top: 1.5em;
}

.add-btn {
    background-color: transparent;
    border: 2px solid var(--color-medium-light-gray);
    color: var(--color-dark-blue);
    font-size: 1.2em;
    font-weight: 600;
    padding: 0.5em 1em;
    border-radius: 25px;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
}

.add-btn:hover {
    border-color: var(--color-dark-blue);
    transform: scale(1.05);
}

.benefit-actions {
    display: flex;
    align-items: center;
    gap: 1em;
}

.delete {
    color: var(--color-medium-red);
    cursor: pointer;
    font-size: 1.2em;
    transition: all 0.2s ease-in-out;
}

.delete:hover {
    color: var(--color-light-red);
}
</style>
